// Long-form help, mirroring the eight categories in the marketing support doc.
// Tours cover orientation; these articles carry the detail that would make a
// tour too long -- notably the Columns, Find/Library and PDF export settings.
const helpContent = [
  {
    id: 'project-setup',
    title: 'Project set up',
    articles: [
      {
        id: 'new-schedule',
        title: 'How to set up a new schedule',
        steps: [
          'Open the project page and click "Add New" in the Schedules section.',
          'Name the schedule and pick a Master template, or switch the Masters filter to No to start from a previous project.',
          'Open "More settings" to choose which columns and data come across, then click Create.',
        ],
      },
      {
        id: 'add-users',
        title: 'Adding users to a schedule',
        steps: [
          'Make sure the company exists first: Companies → Add New, enter the details, verify, then create.',
          'Invite the user: Users → invite to a company, enter their email and assign them. They receive a registration email.',
          'In the schedule, open Settings → Members, search for their name, add them and set their permission level.',
        ],
      },
      {
        id: 'revit-link',
        title: 'Linking the project to a Revit model',
        steps: [
          'On the project set up page, fill in the Revit project number field.',
          'Find that number in Revit under Manage → Settings → Project Information.',
          'The project then appears in the EarlyPlugin for Revit projects page.',
        ],
      },
    ],
  },
  {
    id: 'layout',
    title: 'Customise the schedule layout',
    articles: [
      {
        id: 'minimise',
        title: 'Making more room on screen',
        steps: [
          'Toggle the round button at the top left to expand or collapse the main menu.',
          'Toggle the arrow above the navigation pane to expand or collapse it.',
          'Drag the edge of a column header to change its width.',
          'Drag a row handle to move a row up or down.',
        ],
      },
      {
        id: 'columns',
        title: 'Adding and customising columns',
        steps: [
          'Open Settings → Columns.',
          'Click "Add Column", type a name, and choose the column type.',
          'Drag columns to reorder them, and toggle any column on or off.',
        ],
        note: 'Column types: Text (single line), Multiline (longer notes), Spec (links to the product database), Image, Checkbox, Code (automated code generation), Revision (tracking), and Links (certification and compliance).',
      },
    ],
  },
  {
    id: 'operations',
    title: 'General operations',
    articles: [
      {
        id: 'row-menu',
        title: 'Using the row menu',
        steps: [
          'Click the row menu icon at the left of any row.',
          'Add a row below, copy, cut, paste or delete.',
          'Use Indent and Outdent to change the heading level.',
          'Open Library to import items from previous projects.',
        ],
        note: 'Enable the Batch menu to act on several rows at once. Only heading level 2 rows offer code autofill.',
      },
      {
        id: 'navigation',
        title: 'Using the navigation pane',
        steps: [
          'Click a main category, such as "CLD – Cladding", to jump to that section.',
          'Click the expand arrow to drill into subcategories.',
          'Expand again to reach individual line items.',
        ],
      },
    ],
  },
  {
    id: 'commenting',
    title: 'Commenting',
    articles: [
      {
        id: 'make-comment',
        title: 'Adding and addressing comments',
        steps: [
          'Click the comment icon at the left of a row and type your comment.',
          'Type @ and choose a member to direct it at someone specific.',
          'Rows with open comments show a filled green icon — open it, reply, and close it out.',
        ],
      },
      {
        id: 'navigate-comments',
        title: 'Navigating all comments',
        steps: [
          'Open the Comments menu at the top of the schedule.',
          'Switch between Incoming, Outgoing and Resolved.',
          'Click any comment to jump straight to that item.',
        ],
      },
    ],
  },
  {
    id: 'products',
    title: 'Products and codes',
    articles: [
      {
        id: 'coding',
        title: 'Assigning a code',
        steps: [
          'Main categories use a three letter code, for example CLD for cladding.',
          'Subcategories extend it numerically, for example CLD-100 for curtain wall.',
          'Items get a unique number, for example CLD-101 and CLD-102.',
          'Click the autofill icon on a subcategory row to assign codes automatically.',
        ],
        note: 'You can edit the main and subcategory headings, or change an individual code directly in the Code column.',
      },
      {
        id: 'add-product',
        title: 'Adding a product',
        steps: [
          'Tick the row checkbox to include the item in your schedule.',
          'Open the product catalogue and browse in list or grid view.',
          'Preview an item to see its description, image, manufacturer notes and certification links.',
          'Add it to the row to confirm your selection.',
        ],
        note: 'Star a product to mark it as a favourite for future projects. If your product is not listed, request it and the research team will validate and add it.',
      },
      {
        id: 'edit-description',
        title: 'Editing and formatting a description',
        steps: [
          'Click the edit icon to open the editor.',
          'Change the text freely. Asterisks (***) mark key variables to confirm.',
          'Use Format for heading styles, or Tab and Shift+Tab to indent clauses.',
          'Apply bold, italics or underline, insert predefined terms, and use placeholders for manufacturers and products.',
        ],
      },
      {
        id: 'images-certs',
        title: 'Images and certification links',
        steps: [
          'Selecting a catalogue product brings its image across automatically.',
          'For a custom image, drag a JPEG or PNG into the image cell, or click the cell to browse.',
          'In the Certifications column, add a name such as "Slip rating" and paste the URL.',
        ],
      },
    ],
  },
  {
    id: 'search',
    title: 'Search and filter',
    articles: [
      {
        id: 'filtering',
        title: 'Filtering columns',
        steps: [
          'Click the checkbox column header to filter by selection: tick for selected only, dash for everything, blank for unselected only.',
          'Start typing in a column header to filter by partial or full words.',
          'Combine filters across several columns to narrow things further.',
        ],
      },
      {
        id: 'find-library',
        title: 'Finding items and reusing previous projects',
        steps: [
          'Use the search field at the top of the page — it matches partial and full words, and shows a result count you can step through.',
          'Use the Find menu to go to a specific line, or to search across schedules.',
          'In the row menu, open Library to search previous projects and import items.',
        ],
      },
    ],
  },
  {
    id: 'revisions',
    title: 'Revisions and tracking',
    articles: [
      {
        id: 'revisions',
        title: 'Setting revisions and comparing them',
        steps: [
          'Open the Revisions menu at the top of the schedule.',
          'Add a revision name, such as "Draft Issue", give it a label, and add it.',
          'To compare, tick the two revisions you want and close the menu.',
        ],
        note: 'The schedule is read-only while comparing. Untick the comparisons and select Current Changes to edit again.',
      },
    ],
  },
  {
    id: 'exporting',
    title: 'Exporting',
    articles: [
      {
        id: 'export-pdf',
        title: 'Exporting to PDF',
        steps: [
          'Open the Export menu and choose PDF.',
          'Start from a saved template, or customise the layout.',
          'Filter which columns and revisions are included.',
          'Click Export PDF, then choose page size and scale.',
        ],
      },
      {
        id: 'export-layout',
        title: 'Column, row, header and footer settings',
        steps: [
          'Columns: set spacing by dragging, group columns together, hide them with the eye icon, and reorder by dragging.',
          'Use the text edit icon for font size, alignment and colour, and the column style for vertical lines.',
          'Rows: adjust spacing, line weight, font size, alignment and colour.',
          'Headers and footers: customise left, middle and right, inserting text, logos and images.',
        ],
        note: 'Save your settings for this schedule only, or create a template available to every project.',
      },
    ],
  },
];

export default helpContent;
